import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getComparativa, getEjercicios, getEmpresa, getProyeccionResultado, getMargenBrutoProyectado, getBalance, getDfn } from "@/lib/api";
import { formatCurrency, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TABLA_ANIOS, ColsAnios } from "@/components/ui/tabla-anios";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, GitCompare, Info } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  LabelList,
} from "recharts";
import { CHART, axisProps, gridProps, ChartGradient, ChartTooltip, ChartLegend, lineCursor, fmtCompact } from "@/components/ui/chart-kit";

export default function ComparativaPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([]);
  const [comparativa, setComparativa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingComparativa, setLoadingComparativa] = useState(false);
  // Histórico + proyectado en todas partes (regla 30-09): el consolidado de
  // proyección aporta los años futuros como columnas OPCIONALES, siempre
  // diferenciadas en amarillo "Proy.". Empresa sin proyección → todo como antes.
  const [proy, setProy] = useState(null);
  const [dfnData, setDfnData] = useState(null);
  const [margenProy, setMargenProy] = useState(null);
  const [selectedProyAnios, setSelectedProyAnios] = useState([]);
  // Balance histórico del año base del modelo (ancla del reparto por grupos).
  const [balanceBase, setBalanceBase] = useState(null);
  // Apartado visible (Comparativa / Rentabilidad / Ratios financieros) y bloque
  // de ratios: en la URL, así la selección de años se conserva al cambiar.
  const [searchParams, setSearchParams] = useSearchParams();
  const vista = searchParams.get("vista") || "comparativa";
  const bloque = searchParams.get("bloque") || "liquidez";
  // Base del año parcial (p. ej. 2026 a 30/09): un único selector para toda
  // la pantalla — acumulado real o previsión de cierre del modelo.
  const [base2026, setBase2026] = useState("acumulado");

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const loadData = async () => {
    try {
      const [empresaRes, ejerciciosRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
      ]);
      setEmpresa(empresaRes.data);
      const ejerciciosConDatos = ejerciciosRes.data.filter((e) => e.total_cuentas > 0);
      setEjercicios(ejerciciosConDatos);
      
      // Auto-select all years
      if (ejerciciosConDatos.length >= 2) {
        const anios = ejerciciosConDatos.map((e) => e.anio).sort();
        setSelectedAnios(anios);
      }
      // Proyección (tolerante: sin proyección, la página queda como siempre).
      try {
        const [proyRes, mbRes, dfnRes] = await Promise.all([
          getProyeccionResultado(empresaId),
          getMargenBrutoProyectado(empresaId).catch(() => ({ data: null })),
          // DFN del backend (domain/dfn.py): UNA fuente para la columna
          // proyectada (fase 2 de PLAN_DFN.md); sin ella, N/D.
          getDfn(empresaId).catch(() => ({ data: null })),
        ]);
        setProy(proyRes.data);
        setMargenProy(mbRes.data);
        setDfnData(dfnRes.data);
        setSelectedProyAnios(proyRes.data?.anios_proyectados || []);
        // Balance histórico del AÑO BASE del modelo: ancla del reparto exacto
        // de los grupos corriente/no corriente en las columnas proyectadas
        // (el resto no modelado va PLANO al año base — supuesto del modelo).
        const base = [...(proyRes.data?.anios_historicos || [])].sort((x, y) => x - y).pop();
        const ejBase = ejerciciosConDatos.find((e) => e.anio === base);
        if (ejBase) {
          const bRes = await getBalance(ejBase.id);
          setBalanceBase({ anio: base, data: bRes.data });
        }
      } catch {
        setProy(null);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
      navigate("/empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAnios.length >= 1) {
      loadComparativa();
    } else {
      setComparativa([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnios]);

  const loadComparativa = async () => {
    setLoadingComparativa(true);
    try {
      const response = await getComparativa(empresaId, selectedAnios.join(","));
      setComparativa(response.data.sort((a, b) => a.anio - b.anio));
    } catch (error) {
      toast.error("Error al cargar comparativa");
    } finally {
      setLoadingComparativa(false);
    }
  };

  const toggleAnio = (anio) => {
    setSelectedAnios((prev) =>
      prev.includes(anio) ? prev.filter((a) => a !== anio) : [...prev, anio].sort()
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ── Columnas PROYECTADAS con el MISMO shape que las históricas ───────────
  // Fuente única: el consolidado (pyg_completo/balance/kpis), el margen bruto
  // proyectado y el reparto EXACTO por grupos (corriente/no corriente) con el
  // balance del año base — el resto no modelado va PLANO al año base, que es
  // el supuesto literal del modelo. CADA ratio usa LA MISMA fórmula que el
  // dashboard histórico (domain/dashboard.py): comparar con fórmulas
  // distintas sería mentir. Lo no computable queda null → "N/D".
  const aniosProy = proy?.anios_proyectados || [];
  const filaPygProy = (concepto, a) =>
    (proy?.pyg_completo || []).find((f) => f.concepto === concepto)?.por_anio?.[String(a)];
  const filaPartidaProy = (prefijo, a) =>
    (proy?.pyg || []).find((f) => String(f.partida).startsWith(prefijo))?.por_anio?.[String(a)];
  const partidasBalProy = proy?.balance?.partidas || [];
  const balProy = (concepto, a) =>
    partidasBalProy.find((p) => p.concepto === concepto)?.por_anio?.[String(a)];
  const sumaGrupoProy = (grupo, a) =>
    partidasBalProy
      .filter((p) => p.grupo === grupo && !p.sub)
      .reduce((acc, p) => acc + (p.por_anio?.[String(a)] ?? 0), 0);

  // Reparto por grupos con el balance del año base (mismas reglas que en
  // Comparativa Años): AC/PC/PNC = partidas modeladas a su valor + resto
  // PLANO al año base, con candado al euro contra el total del consolidado.
  const anioBaseModelo = balanceBase?.anio;
  const buscarEnArbol = (items, partida) => {
    for (const it of items || []) {
      if (it.partida === partida) return it;
      const f = buscarEnArbol(it.hijos, partida);
      if (f) return f;
    }
    return null;
  };
  const hBase = (seccion, partida) =>
    buscarEnArbol(balanceBase?.data?.[seccion]?.items, partida)?.saldo ?? 0;
  const otrosPlano = (concepto, a) => {
    const va = balProy(concepto, a);
    const vb = balProy(concepto, anioBaseModelo);
    return va != null && vb != null && Math.abs(va - vb) <= 1;
  };
  const gruposProy = (a) => {
    if (!balanceBase || !otrosPlano("Otros activos", a) || !otrosPlano("Otros pasivos", a)) return null;
    const sec = "patrimonio_neto_pasivo";
    const ac = (balProy("Existencias", a) ?? 0) + (balProy("Clientes", a) ?? 0) +
      (balProy("Tesorería", a) ?? 0) + hBase("activo", "B) ACTIVO CORRIENTE") -
      hBase("activo", "I. Existencias") -
      hBase("activo", "II. Deudores comerciales y otras cuentas a cobrar") -
      hBase("activo", "VI. Efectivo y otros activos líquidos equivalentes");
    const pnc = (balProy("Deuda financiera LP", a) ?? 0) + hBase(sec, "B) PASIVO NO CORRIENTE") -
      hBase(sec, "II. Deudas a largo plazo") -
      hBase(sec, "III. Deudas con empresas del grupo y asociadas a l/p");
    const pc = (balProy("Deuda financiera CP", a) ?? 0) + (balProy("Proveedores", a) ?? 0) +
      hBase(sec, "C) PASIVO CORRIENTE") -
      hBase(sec, "II. Deudas a corto plazo") -
      hBase(sec, "III. Deudas con empresas del grupo y asociadas a c/p") -
      hBase(sec, "IV. Acreedores comerciales y otras cuentas a pagar");
    // Candado al euro: si el reparto no casa con los totales del consolidado,
    // mejor N/D que una cifra que no cuadra.
    const anc = (balProy("Inmovilizado neto", a) ?? 0) + hBase("activo", "A) ACTIVO NO CORRIENTE") -
      ["I. Inmovilizado intangible", "II. Inmovilizado material", "III. Inversiones inmobiliarias"]
        .reduce((s, p) => s + hBase("activo", p), 0);
    const pn0 = balProy("Patrimonio neto", a) ?? 0;
    if (Math.abs(anc + ac - sumaGrupoProy("activo", a)) > 1) return null;
    if (Math.abs(pn0 + pnc + pc - sumaGrupoProy("pasivo_pn", a)) > 1) return null;
    return { ac, pc, pnc };
  };

  const columnasProy = aniosProy
    .filter((a) => selectedProyAnios.includes(a))
    .sort((a, b) => a - b)
    .map((a) => {
      const k = String(a);
      const ventas = proy?.kpis?.ventas?.por_anio?.[k] ?? null;
      const ebitda = proy?.kpis?.ebitda?.por_anio?.[k] ?? null;
      const neto = filaPygProy("Resultado neto", a);
      const amortizacion = filaPygProy("Amortización", a);
      const activo = sumaGrupoProy("activo", a) || null;
      const pn = balProy("Patrimonio neto", a);
      const mb = margenProy?.proyectado?.[k];
      const g = gruposProy(a);
      const existencias = balProy("Existencias", a);
      const clientes = balProy("Clientes", a);
      const proveedores = balProy("Proveedores", a);
      const tesoreria = balProy("Tesorería", a);
      const deudaFin = (balProy("Deuda financiera LP", a) ?? 0) + (balProy("Deuda financiera CP", a) ?? 0);
      // Mismas convenciones que el dashboard histórico:
      // EBITDA de ratios = ajustado → en proyección, el NORMALIZADO si hay
      // ajustes definidos (su espejo); si no, el EBITDA del modelo.
      const normalizado = proy?.ebitda_normalizado?.por_anio?.[k] ?? null;
      const ebitdaRatios = normalizado ?? ebitda;
      // cash_flow = neto + amortización (fórmula del dashboard, NO Δtesorería)
      const cashFlow = neto != null && amortizacion != null ? neto + amortizacion : null;
      // compras/coste de ventas = aprovisionamientos (partida 4, fallback del
      // dashboard); personal = partida 6; gastos financieros = partida 14.
      const compras = Math.abs(filaPartidaProy("4. ", a) ?? 0) || null;
      // personal es NUMERADOR (0 € de personal ⇒ ratio 0%, dato real del
      // modelo, no un N/D); compras y gastosFin son divisores: 0 → null.
      const personalRaw = filaPartidaProy("6. ", a);
      const personal = personalRaw != null ? Math.abs(personalRaw) : null;
      const gastosFin = Math.abs(filaPartidaProy("14. ", a) ?? 0) || null;
      // capex histórico es un PROXY = amortización del ejercicio → misma
      // convención en proyección para que la columna sea comparable.
      const capexProxy = amortizacion;
      // DFN y múltiplo de la MISMA fuente que el dashboard histórico y la
      // página DFN (`dfn_balance` = deuda LP + CP − tesorería; ratio sobre el
      // EBITDA ajustado/normalizado, N/D con EBITDA ≤ 0). Ya no se calcula aquí.
      const dfn = dfnData?.filas?.dfn_balance?.[k] ?? null;
      const dfnEbitdaProy = dfnData?.filas?.dfn_balance_ebitda_ajustado?.[k] ?? null;
      // Cascada del módulo NOF por bloque y año (misma fuente que la pestaña
      // NOF): para la NOF ajustada y los plazos proyectados en modo "días".
      const esDias = proy?.nof_ratios?.modo === "dias";
      const _nd = (b) => proy?.nof_detalle?.[b]?.por_anio?.[k] || {};
      const ndc = _nd("clientes");
      const nde = _nd("existencias");
      const ndp = _nd("proveedores");
      return {
        anio: a,
        proyectado: true,
        kpis: {
          ventas,
          ebitda,
          margen_ebitda: proy?.kpis?.ebitda?.margen_por_anio?.[k] ?? null,
          // EBITDA Ajustado ⇔ EBITDA NORMALIZADO (sus add-backs a futuro).
          // Sin ajustes definidos = EBITDA a secas: es EXACTAMENTE lo que
          // hace la fórmula histórica (ebitda.py: ajustado = base + Σajustes,
          // con Σ=0 ⇒ base) — N/D aquí era un hueco falso (13-oct).
          ebitda_ajustado: ebitdaRatios,
          margen_ebitda_ajustado: ebitdaRatios != null && ventas ? (ebitdaRatios / ventas) * 100 : null,
          ebit: filaPygProy("EBIT", a),
          margen_ebit: ventas && filaPygProy("EBIT", a) != null ? (filaPygProy("EBIT", a) / ventas) * 100 : null,
          resultado_neto: neto,
          margen_neto: filaPygProy("Margen neto %", a),
          cash_flow: cashFlow,
        },
        ratios: {
          // Misma regla de N/D que el dashboard (domain/dashboard.py): sin
          // activo o con PN ≤ 0 el ratio no tiene sentido.
          roa: neto != null && activo > 0 ? (neto / activo) * 100 : null,
          roe: neto != null && pn > 0 ? (neto / pn) * 100 : null,
          margen_bruto: mb?.margen_bruto ?? null,
          margen_bruto_pct: mb?.margen_bruto_pct ?? null,
        },
        ratios_liquidez: {
          liquidez_principal: g && g.pc > 0 ? g.ac / g.pc : null,
          fondo_maniobra: g ? g.ac - g.pc : null,
          nof: existencias != null && clientes != null && proveedores != null
            ? existencias + clientes - proveedores : null,
          // NOF ajustada proyectada: de la cascada del módulo (bruto −
          // excluido por bloque), misma fuente que la pestaña NOF.
          nof_ajustada: ndc.ajustado != null && nde.ajustado != null && ndp.ajustado != null
            ? nde.ajustado + ndc.ajustado - ndp.ajustado : null,
        },
        ratios_solvencia: {
          solvencia_principal: g && activo && g.pnc + g.pc > 0 ? activo / (g.pnc + g.pc) : null,
        },
        ratios_endeudamiento: {
          // Denominador ≤ 0 → N/D (misma regla que dashboard.py y la página DFN).
          dfn_ebitda: dfnEbitdaProy,
          deuda_cash_flow: cashFlow > 0 ? deudaFin / cashFlow : null,
          endeudamiento_lp: g && pn > 0 ? g.pnc / pn : null,
          endeudamiento_cp: g && pn > 0 ? g.pc / pn : null,
          ebitda_gastos_financieros: gastosFin && ebitdaRatios != null ? ebitdaRatios / gastosFin : null,
          capex_ebitda: capexProxy != null && ebitdaRatios ? (capexProxy / ebitdaRatios) * 100 : null,
        },
        ratios_rotacion: {
          // Con el módulo en modo "días", los plazos proyectados salen de la
          // cascada del motor NOF (bases limpias 60+61/62 y ventas sin las
          // excluidas de la base); si no, fórmula clásica sobre saldos.
          plazo_cobro: esDias ? ndc.dia_general ?? null
            : (ventas && clientes != null ? (clientes / ventas) * 365 : null),
          // PMP con la BASE CONFIGURADA (nof.base_pmp), igual que el dashboard
          // (revisión externa oct-2026, #6).
          plazo_pago: esDias ? ndp.dia_pmp_general ?? ndp.dia_general ?? null
            : (compras && proveedores != null ? (proveedores / compras) * 365 : null),
          plazo_existencias: esDias ? nde.dia_general ?? null
            : (compras && existencias != null ? (existencias / compras) * 365 : null),
          plazo_cobro_ajustado: esDias ? ndc.dia_ajustado ?? null : null,
          plazo_pago_ajustado: esDias ? ndp.dia_pmp_ajustado ?? ndp.dia_ajustado ?? null : null,
          plazo_existencias_ajustado: esDias ? nde.dia_ajustado ?? null : null,
        },
        ratios_personal: {
          personal_ventas: ventas && personal != null ? (personal / ventas) * 100 : null,
        },
      };
    });
  // Dedupe del año que es ejercicio real Y proyectado:
  //  - real COMPLETO → gana el ejercicio real (Héctor 13-oct; la columna
  //    sintética tiene N/D en lo no derivable y nunca debe taparlo);
  //  - real PARCIAL  → dos columnas contiguas, "Acumulado" (real, con fecha de
  //    corte) y "Previsión cierre" (consolidado) — regla P14 de Margen Bruto
  //    extendida a la Comparativa (documento de cambios visuales, oct-2026).
  const realPorAnio = new Map(comparativa.map((c) => [c.anio, c]));
  const anioParcial = comparativa.find((c) => c.periodo?.parcial)?.anio ?? null;
  const proyVisibles = columnasProy
    .filter((c) => {
      const real = realPorAnio.get(c.anio);
      return !real || real.periodo?.parcial;
    })
    .map((c) => (c.anio === anioParcial ? { ...c, prevision: true } : c));
  // Año parcial sin previsión en el modelo (el año base ES el parcial): la
  // columna "Previsión cierre" existe pero vacía ("—"), nunca inventada.
  const previsionVacia =
    anioParcial != null && realPorAnio.has(anioParcial) && selectedProyAnios.length > 0 &&
    !columnasProy.some((c) => c.anio === anioParcial)
      ? [{ anio: anioParcial, proyectado: true, prevision: true, vacia: true }]
      : [];
  const columnas = [
    ...comparativa,
    ...[...previsionVacia, ...proyVisibles].sort((a, b) => a.anio - b.anio),
  ];
  const totalSeleccion = selectedAnios.length + selectedProyAnios.length;

  // Serie temporal "una columna por año" según la Base 2026 elegida: la usan
  // el crecimiento interanual, las gráficas y las pestañas Rentabilidad y
  // Ratios. Solo cambia el año parcial (acumulado real vs previsión de cierre).
  const serie = [...new Set(columnas.map((c) => c.anio))]
    .sort((a, b) => a - b)
    .map((a) => {
      const cands = columnas.filter((c) => c.anio === a);
      if (cands.length === 1) return cands[0];
      return base2026 === "prevision" ? cands.find((c) => c.prevision) : cands.find((c) => !c.proyectado);
    });
  const fechaCorte = (() => {
    const ej = ejercicios.find((e) => e.anio === anioParcial);
    if (!ej?.fecha_fin) return null;
    const [y, m, d] = String(ej.fecha_fin).slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  })();
  const serieUsaAcumulado = anioParcial != null && serie.some((c) => c.anio === anioParcial && !c.proyectado);
  const seriePrevisionVacia = serie.some((c) => c.vacia);

  const cambiarVista = (v) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("vista", v);
    setSearchParams(sp, { replace: true });
  };
  const cambiarBloque = (b) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("bloque", b);
    setSearchParams(sp, { replace: true });
  };

  const ctx = { anioParcial, fechaCorte, base: base2026, setBase: setBase2026 };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Cabecera */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(`/empresas/${empresaId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">Comparativa Ratios</h1>
          <p className="text-sm text-muted-foreground">{empresa?.nombre}</p>
        </div>
      </div>

      {/* Menú de la pantalla: tres apartados, uno visible cada vez */}
      <div className="pill-group" role="tablist" aria-label="Apartados de la comparativa">
        {VISTAS.map((v) => (
          <button
            key={v.id}
            role="tab"
            aria-selected={vista === v.id}
            onClick={() => cambiarVista(v.id)}
            className={cn("pill px-5", vista === v.id && "pill-active")}
            data-testid={`vista-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Selector de ejercicios + Base 2026 (común a toda la pantalla) */}
      <Card data-testid="year-selector">
        <CardContent className="flex flex-wrap items-center gap-x-5 gap-y-3 p-4">
          <span className="text-[13px] font-semibold">Seleccionar ejercicios</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Histórico:</span>
            {ejercicios
              .slice()
              .sort((a, b) => a.anio - b.anio)
              .map((ej) => (
                <label key={ej.anio} className="flex cursor-pointer items-center gap-1.5 text-[13px]">
                  <Checkbox
                    checked={selectedAnios.includes(ej.anio)}
                    onCheckedChange={() => toggleAnio(ej.anio)}
                    data-testid={`select-anio-${ej.anio}`}
                  />
                  {ej.anio}
                  {ej.anio === anioParcial && <span className="text-muted-foreground">· Acumulado</span>}
                </label>
              ))}
          </div>
          {aniosProy.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-l border-border pl-5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-proy">Proyección:</span>
              {aniosProy.map((a) => (
                <label key={a} className="flex cursor-pointer items-center gap-1.5 text-[13px]">
                  <Checkbox
                    checked={selectedProyAnios.includes(a)}
                    onCheckedChange={() =>
                      setSelectedProyAnios((prev) =>
                        prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a].sort()
                      )
                    }
                    data-testid={`select-anio-proy-${a}`}
                  />
                  {a}
                  {a === anioParcial && <span className="text-muted-foreground">· Previsión cierre</span>}
                </label>
              ))}
            </div>
          )}
          <div className="flex-1" />
          {aniosProy.length > 0 && (
            <div className="pill-group">
              <button className="pill" data-testid="sel-todo"
                onClick={() => { setSelectedAnios(ejercicios.map((e) => e.anio).sort()); setSelectedProyAnios([...aniosProy]); }}>
                Todos
              </button>
              <button className="pill" data-testid="sel-historico"
                onClick={() => { setSelectedAnios(ejercicios.map((e) => e.anio).sort()); setSelectedProyAnios([]); }}>
                Solo histórico
              </button>
              <button className="pill" data-testid="sel-proyeccion"
                onClick={() => { setSelectedAnios([]); setSelectedProyAnios([...aniosProy]); }}>
                Solo proyección
              </button>
            </div>
          )}
          {anioParcial != null && vista === "comparativa" && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-medium text-muted-foreground">Base {anioParcial}:</span>
              <div className="pill-group" data-testid="base-parcial">
                <button className={cn("pill", base2026 === "acumulado" && "pill-active")} onClick={() => setBase2026("acumulado")}>
                  Histórico acumulado
                </button>
                <button className={cn("pill", base2026 === "prevision" && "pill-active")} onClick={() => setBase2026("prevision")}>
                  Previsión de cierre
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {totalSeleccion === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <GitCompare className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Selecciona al menos un año (histórico o proyectado) para ver los datos</p>
          </CardContent>
        </Card>
      ) : loadingComparativa ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
      ) : vista === "rentabilidad" ? (
        <VistaRentabilidad serie={serie} ctx={ctx} serieUsaAcumulado={serieUsaAcumulado} previsionVacia={seriePrevisionVacia} />
      ) : vista === "ratios" ? (
        <VistaRatios serie={serie} ctx={ctx} bloque={bloque} onBloque={cambiarBloque}
          serieUsaAcumulado={serieUsaAcumulado} previsionVacia={seriePrevisionVacia} />
      ) : (
        <>
          {/* Tabla comparativa */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tabla comparativa</CardTitle>
              <CardDescription>Importes en miles de €</CardDescription>
            </CardHeader>
            <CardContent>
              <TablaAnios columnas={columnas} ctx={ctx} filas={FILAS_COMPARATIVA} />
            </CardContent>
          </Card>

          {/* Crecimiento interanual */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Crecimiento interanual</CardTitle>
              <CardDescription>Variación respecto al ejercicio anterior · %</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {serieUsaAcumulado && (
                <AvisoParcial>
                  Base seleccionada: <b>{anioParcial} acumulado</b> · comparación entre periodos de distinta duración (sin anualizar).
                </AvisoParcial>
              )}
              <TablaCrecimiento columnas={columnas} serie={serie} ctx={ctx} />
            </CardContent>
          </Card>

          {/* Evolución financiera a todo el ancho */}
          <GraficaEvolucion serie={serie} ctx={ctx} serieUsaAcumulado={serieUsaAcumulado} />
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuración y piezas de presentación
// ─────────────────────────────────────────────────────────────────────────────

const VISTAS = [
  { id: "comparativa", label: "Comparativa" },
  { id: "rentabilidad", label: "Rentabilidad" },
  { id: "ratios", label: "Ratios financieros" },
];

const nf0 = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0, useGrouping: "always" });
const nf1 = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: "always" });
const nf2 = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });
const esNum = (v) => v !== null && v !== undefined && !Number.isNaN(v);

// Formatos de celda. null = no computable → "N/D" (política de N/D); la
// columna de previsión vacía y el ROI pendiente pintan "—".
const FMT = {
  miles: (v) => nf0.format(v / 1000),
  eur: (v) => formatCurrency(v),
  pct: (v) => `${nf1.format(v)} %`,
  veces: (v) => nf2.format(v),
  x: (v) => `${nf2.format(v)}x`,
  dias: (v) => `${nf0.format(v)} días`,
};
const fmtCelda = (v, fmt, c) => {
  if (c?.vacia) return "—";
  if (!esNum(v)) return "N/D";
  return FMT[fmt](v);
};

// 0 € es un dato; null = N/D (no se sustituye por el resultado neto bajo la etiqueta "Cash flow").
const cashFlowDe = (c) => c.kpis?.cash_flow ?? null;

const FILAS_COMPARATIVA = [
  { label: "Ventas", get: (c) => c.kpis?.ventas, fmt: "miles", bold: true },
  { label: "Margen bruto (€)", get: (c) => c.ratios?.margen_bruto, fmt: "miles" },
  { label: "Margen bruto %", get: (c) => c.ratios?.margen_bruto_pct, fmt: "pct", sub: true },
  { label: "EBITDA", get: (c) => c.kpis?.ebitda, fmt: "miles", bold: true },
  { label: "Margen EBITDA", get: (c) => c.kpis?.margen_ebitda, fmt: "pct", sub: true },
  { label: "EBITDA ajustado", get: (c) => c.kpis?.ebitda_ajustado, fmt: "miles", bold: true },
  { label: "Margen EBITDA ajust.", get: (c) => c.kpis?.margen_ebitda_ajustado, fmt: "pct", sub: true },
  { label: "EBIT", get: (c) => c.kpis?.ebit, fmt: "miles" },
  { label: "Resultado neto", get: (c) => c.kpis?.resultado_neto, fmt: "miles", bold: true },
  { label: "Cash flow", get: cashFlowDe, fmt: "miles" },
];

const FILAS_CRECIMIENTO = [
  { label: "Ventas", get: (c) => c.kpis?.ventas },
  { label: "Margen bruto", get: (c) => c.ratios?.margen_bruto },
  { label: "EBITDA", get: (c) => c.kpis?.ebitda },
  { label: "EBITDA ajustado", get: (c) => c.kpis?.ebitda_ajustado },
  { label: "EBIT", get: (c) => c.kpis?.ebit },
  { label: "Resultado neto", get: (c) => c.kpis?.resultado_neto },
  { label: "Cash flow", get: cashFlowDe },
];

function etiquetaColumna(c, ctx) {
  if (c.anio === ctx.anioParcial && !c.proyectado) return `${c.anio} · Acumulado`;
  if (c.prevision) return `${c.anio} · Previsión cierre`;
  return String(c.anio);
}

function AvisoParcial({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-[hsl(var(--proy)/0.35)] bg-proy px-3 py-2 text-[12px] text-foreground/80">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-proy" />
      <span>{children}</span>
    </div>
  );
}

// Tabla por años con cabeceras agrupadas HISTÓRICO / PROYECCIÓN.
function TablaAnios({ columnas, ctx, filas }) {
  const hist = columnas.filter((c) => !c.proyectado);
  const proy = columnas.filter((c) => c.proyectado);
  return (
    <div className="overflow-x-auto rounded-xl border border-border/60 bg-[hsl(var(--card)/0.7)]">
      <table className={cn(TABLA_ANIOS, "text-[13px]")}>
        <ColsAnios n={columnas.length} />
        <thead>
          <tr className="text-[11px] uppercase tracking-wider">
            <th rowSpan={2} className="border-b border-border/60 px-4 py-2 text-left font-medium text-muted-foreground">Concepto</th>
            {hist.length > 0 && (
              <th colSpan={hist.length} className="border-b border-border/60 bg-muted/60 px-3 py-1.5 text-center font-semibold text-muted-foreground">
                Histórico
              </th>
            )}
            {proy.length > 0 && (
              <th colSpan={proy.length} className="border-b border-border/60 bg-proy-strong px-3 py-1.5 text-center font-semibold text-proy">
                Proyección
              </th>
            )}
          </tr>
          <tr>
            {columnas.map((c) => {
              const parcialReal = c.anio === ctx.anioParcial && !c.proyectado;
              return (
                <th
                  key={`${c.anio}-${c.proyectado ? "p" : "h"}`}
                  className={cn(
                    "border-b border-border/60 px-3 py-2 text-right text-[12px] font-medium",
                    c.proyectado ? "bg-proy text-proy" : "text-foreground",
                    c.prevision && "font-semibold"
                  )}
                >
                  <div>{etiquetaColumna(c, ctx)}</div>
                  {parcialReal && ctx.fechaCorte && (
                    <div className="text-[10px] font-normal text-muted-foreground">Corte {ctx.fechaCorte}</div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => f.grupo ? (
            <tr key={`g-${f.grupo}`} className="border-b border-border/40">
              <td colSpan={columnas.length + 1} className="bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {f.grupo}
              </td>
            </tr>
          ) : (
            <tr key={f.label} className="border-b border-border/40 last:border-0 hover:bg-foreground/[0.025]">
              <td className={cn("px-4 py-2", f.bold && "font-semibold", f.sub && "pl-8 text-muted-foreground")}>
                {f.label}
              </td>
              {columnas.map((c) => {
                const v = f.get(c);
                return (
                  <td
                    key={`${c.anio}-${c.proyectado ? "p" : "h"}`}
                    className={cn(
                      "px-3 py-2 text-right tabular-nums",
                      c.proyectado && "bg-proy",
                      f.bold && "font-semibold",
                      f.sub && "text-muted-foreground",
                      f.color && esNum(v) && !c.vacia && f.color(v)
                    )}
                  >
                    {f.dash ? "—" : fmtCelda(v, f.fmt, c)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Crecimiento con las MISMAS columnas que la tabla comparativa de encima (para
// que las dos queden alineadas): cada columna se compara con el año anterior
// de la serie (con la base elegida para el año parcial); el primer año no
// tiene contra qué compararse ("—").
function TablaCrecimiento({ columnas, serie, ctx }) {
  if (serie.length < 2) {
    return <p className="py-4 text-center text-sm text-muted-foreground">Selecciona al menos dos años para ver el crecimiento.</p>;
  }
  const crec = (cur, prev) => (esNum(cur) && esNum(prev) && prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : null);
  const previo = (c) => serie.find((x) => x.anio === c.anio - 1) || null;
  const clave = (c) => `${c.anio}-${c.proyectado ? "p" : "h"}`;
  return (
    <div className="overflow-x-auto rounded-xl border border-border/60 bg-[hsl(var(--card)/0.7)]">
      <table className={cn(TABLA_ANIOS, "text-[13px]")}>
        <ColsAnios n={columnas.length} />
        <thead>
          <tr>
            <th className="border-b border-border/60 px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Concepto</th>
            {columnas.map((c) => {
              const p = previo(c);
              return (
                <th key={clave(c)} className={cn("border-b border-border/60 px-3 py-2 text-right text-[12px] font-semibold", c.proyectado && "bg-proy text-proy")}>
                  <div>{etiquetaColumna(c, ctx)}</div>
                  <div className="text-[10px] font-normal text-muted-foreground">
                    {p ? `vs ${p.anio}${p.anio === ctx.anioParcial ? (p.proyectado ? " previsión" : " acum.") : ""}` : "—"}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {FILAS_CRECIMIENTO.map((f) => (
            <tr key={f.label} className="border-b border-border/40 last:border-0">
              <td className="px-4 py-2 font-medium">{f.label}</td>
              {columnas.map((c) => {
                const p = previo(c);
                const v = !p || p.vacia || c.vacia ? null : crec(f.get(c), f.get(p));
                return (
                  <td key={clave(c)} className={cn("px-3 py-2 text-right tabular-nums", c.proyectado && "bg-proy",
                    esNum(v) ? (v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400") : "text-muted-foreground")}>
                    {esNum(v) ? `${v >= 0 ? "+" : ""}${nf1.format(v)} %` : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {ctx.anioParcial != null && (
        <p className="px-4 py-2 text-[11px] text-muted-foreground">La base elegida se aplica a los pares con el año parcial. Sin anualización automática.</p>
      )}
    </div>
  );
}

const SERIES_EVOLUCION = [
  { key: "ventas", name: "Ventas", color: CHART.gunmetal, get: (c) => c.kpis?.ventas },
  { key: "ebitda", name: "EBITDA", color: CHART.mostaza, get: (c) => c.kpis?.ebitda },
  { key: "resultado", name: "Resultado neto", color: CHART.bronce, get: (c) => c.kpis?.resultado_neto },
];

function GraficaEvolucion({ serie, ctx, serieUsaAcumulado }) {
  const [visibles, setVisibles] = useState({ ventas: true, ebitda: true, resultado: true });
  const [mostrarValores, setMostrarValores] = useState(false);
  const data = serie.map((c) => {
    const fila = { anio: c.anio === ctx.anioParcial ? `${c.anio}${c.proyectado ? " prev." : " acum."}` : String(c.anio) };
    SERIES_EVOLUCION.forEach((s) => { fila[s.key] = c.vacia ? null : s.get(c) ?? null; });
    return fila;
  });
  const etiqueta = (v) => (esNum(v) ? nf0.format(v / 1000) : "");
  return (
    <Card data-testid="evolution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Evolución financiera</CardTitle>
        <CardDescription>Ventas, EBITDA y Resultado neto por ejercicio</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
          <span className="font-medium text-muted-foreground">Series visibles:</span>
          {SERIES_EVOLUCION.map((s) => (
            <label key={s.key} className="flex cursor-pointer items-center gap-1.5">
              <Checkbox
                checked={visibles[s.key]}
                onCheckedChange={() => setVisibles((p) => ({ ...p, [s.key]: !p[s.key] }))}
                data-testid={`serie-${s.key}`}
              />
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.name}
            </label>
          ))}
          <span className="mx-1 h-5 w-px bg-border" />
          <label className="flex cursor-pointer items-center gap-2">
            <Switch checked={mostrarValores} onCheckedChange={setMostrarValores} data-testid="mostrar-valores" />
            Mostrar valores
          </label>
          {mostrarValores && <span className="text-[11px] text-muted-foreground">Valores en miles de €</span>}
        </div>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 22, right: 18, left: 0, bottom: 0 }}>
              <defs>
                <ChartGradient id="colorVentas" color={CHART.gunmetal} from={0.22} />
                <ChartGradient id="colorEbitda" color={CHART.mostaza} from={0.38} />
                <ChartGradient id="colorResultado" color={CHART.bronce} from={0.25} />
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="anio" {...axisProps} />
              <YAxis tickFormatter={fmtCompact} {...axisProps} width={56} />
              <Tooltip cursor={lineCursor} content={<ChartTooltip formatter={(value) => formatCurrency(value)} />} />
              <Legend content={<ChartLegend />} />
              {SERIES_EVOLUCION.filter((s) => visibles[s.key]).map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2.25}
                  fill={`url(#color${s.key === "ventas" ? "Ventas" : s.key === "ebitda" ? "Ebitda" : "Resultado"})`}
                  dot={mostrarValores ? { r: 3.5, strokeWidth: 0, fill: s.color } : false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls={false}
                >
                  {mostrarValores && (
                    <LabelList dataKey={s.key} position="top" offset={8} formatter={etiqueta}
                      style={{ fontSize: 11, fill: s.color, fontWeight: 600 }} />
                  )}
                </Area>
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {serieUsaAcumulado && (
          <p className="text-[11px] text-muted-foreground">
            {ctx.anioParcial} · Acumulado{ctx.fechaCorte ? ` a ${ctx.fechaCorte}` : ""} (periodo parcial, misma base que el crecimiento interanual).
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Selector de la base del año parcial, junto al título de cada bloque (Word,
// imágenes 4 y 5). Es el MISMO estado que en Comparativa: cambiarlo aquí
// cambia toda la pantalla.
function SelectorBase({ ctx }) {
  if (ctx.anioParcial == null) return null;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium text-muted-foreground">Base {ctx.anioParcial}:</span>
        <div className="pill-group">
          <button className={cn("pill", ctx.base === "acumulado" && "pill-active")} onClick={() => ctx.setBase("acumulado")}>
            Histórico acumulado
          </button>
          <button className={cn("pill", ctx.base === "prevision" && "pill-active")} onClick={() => ctx.setBase("prevision")}>
            Previsión de cierre
          </button>
        </div>
      </div>
      <span className="text-[11px] text-muted-foreground">Se aplica a la tabla y a todas las gráficas.</span>
    </div>
  );
}

// Cabecera de un bloque: título + descripción a la izquierda, base a la derecha.
function CabeceraBloque({ titulo, desc, ctx }) {
  return (
    <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 pb-3">
      <div className="space-y-1">
        <CardTitle className="text-base">{titulo}</CardTitle>
        {desc && <CardDescription>{desc}</CardDescription>}
      </div>
      <SelectorBase ctx={ctx} />
    </CardHeader>
  );
}

const COLORES_LINEA = [CHART.mostaza, CHART.gunmetal, CHART.bronce, CHART.pizarra];

// Gráfica de líneas de UN grupo de indicadores (misma unidad): una serie por
// fila del grupo, en el mismo orden que la tabla.
function GraficaGrupo({ grupo, serie, ctx, serieUsaAcumulado, testId, pendientes = [] }) {
  const lineas = grupo.filas
    .filter((f) => !f.dash)
    .map((f, i) => ({ key: `l${i}`, name: f.corto || f.label, color: COLORES_LINEA[i % COLORES_LINEA.length], get: f.get }));
  const formato = grupo.formato;
  const data = serie.map((c) => {
    const fila = { anio: c.anio === ctx.anioParcial ? `${c.anio}${c.proyectado ? " prev." : " acum."}` : String(c.anio) };
    lineas.forEach((l) => {
      const v = c.vacia ? null : l.get(c);
      fila[l.key] = esNum(v) ? v : null;
    });
    return fila;
  });
  const fmtEje = formato === "miles" ? (v) => nf0.format(v / 1000) : formato === "pct" ? (v) => `${nf0.format(v)} %` : (v) => nf1.format(v);
  const fmtTip = (v) => (formato === "miles" ? formatCurrency(v) : FMT[formato] ? FMT[formato](v) : nf2.format(v));
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{grupo.grafica}</CardTitle>
        <CardDescription>{grupo.unidad}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="anio" {...axisProps} />
              <YAxis tickFormatter={fmtEje} {...axisProps} width={56} />
              <Tooltip cursor={lineCursor} content={<ChartTooltip formatter={(v) => fmtTip(v)} />} />
              <Legend
                content={() => (
                  // Leyenda en el MISMO orden que la tabla (Recharts reordena la suya)
                  <ChartLegend
                    payload={[
                      ...lineas.map((l) => ({ value: l.name, color: l.color })),
                      ...pendientes.map((p) => ({ value: `${p} · pendiente`, color: CHART.muted })),
                    ]}
                  />
                )}
              />
              {lineas.map((l) => (
                <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2.25}
                  dot={{ r: 3.5, strokeWidth: 0, fill: l.color }} activeDot={{ r: 5, strokeWidth: 0 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {serieUsaAcumulado && (
          <p className="text-[11px] text-muted-foreground">
            {ctx.anioParcial} · Acumulado{ctx.fechaCorte ? ` a ${ctx.fechaCorte}` : ""} · Misma base que la tabla.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AvisoSerie({ ctx, serieUsaAcumulado, previsionVacia }) {
  if (!serieUsaAcumulado && !previsionVacia) return null;
  return (
    <AvisoParcial>
      {serieUsaAcumulado ? (
        <>
          <b>{ctx.anioParcial} · Histórico acumulado</b>
          {ctx.fechaCorte ? ` · fecha de corte ${ctx.fechaCorte}` : ""} · periodo parcial: los plazos usan los días reales y los
          ratios que mezclan saldos y flujos van anualizados.
        </>
      ) : (
        <>
          <b>{ctx.anioParcial} · Previsión de cierre</b>: el modelo de proyección arranca en {ctx.anioParcial}, así que no hay
          previsión de cierre para ese año ("—").
        </>
      )}
    </AvisoParcial>
  );
}

// Filas de la tabla de un bloque: cada grupo con su cabecera (título · unidad),
// en el mismo orden que las gráficas de debajo.
const filasDeGrupos = (grupos) =>
  grupos.flatMap((g) => [{ grupo: `${g.titulo} · ${g.unidad}` }, ...g.filas]);

const colorRatio = (v) => (v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400");
const colorDfn = (v) => (v > 4 ? "text-rose-600 dark:text-rose-400" : v > 3 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400");

// Rentabilidad (Word, imagen 4): solo ROE, ROI y ROA.
const GRUPO_RENTABILIDAD = {
  titulo: "Rentabilidad",
  unidad: "%",
  grafica: "Evolución de la rentabilidad",
  formato: "pct",
  filas: [
    { label: "ROE", corto: "ROE", get: (c) => c.ratios?.roe, fmt: "pct", bold: true, color: colorRatio },
    // ROI: fórmula pendiente de definir → siempre "—" (nunca una cifra inventada)
    { label: "ROI", corto: "ROI", get: () => null, fmt: "pct", bold: true, dash: true },
    { label: "ROA", corto: "ROA", get: (c) => c.ratios?.roa, fmt: "pct", bold: true, color: colorRatio },
  ],
};

function VistaRentabilidad({ serie, ctx, serieUsaAcumulado, previsionVacia }) {
  return (
    <div className="space-y-5">
      <Card>
        <CabeceraBloque
          titulo="ROE, ROI y ROA"
          desc="Rentabilidad sobre recursos propios, sobre la inversión y sobre el activo · %"
          ctx={ctx}
        />
        <CardContent className="space-y-3">
          <AvisoSerie ctx={ctx} serieUsaAcumulado={serieUsaAcumulado} previsionVacia={previsionVacia} />
          <TablaAnios columnas={serie} ctx={ctx} filas={GRUPO_RENTABILIDAD.filas} />
          <AvisoParcial>ROI: fórmula y base de inversión pendientes de definir. Se muestra "—" y no se dibuja en la gráfica.</AvisoParcial>
        </CardContent>
      </Card>
      <GraficaGrupo
        grupo={{ ...GRUPO_RENTABILIDAD, unidad: "ROE, ROI y ROA · %" }}
        serie={serie}
        ctx={ctx}
        serieUsaAcumulado={serieUsaAcumulado}
        testId="ratios-chart"
        pendientes={["ROI"]}
      />
    </div>
  );
}

// Ratios financieros (Word, imagen 5): cada bloque se divide en GRUPOS de
// indicadores de la misma unidad. La tabla los muestra con su cabecera y
// debajo va una gráfica por grupo, en el mismo orden: tabla y gráficas se
// leen como una sola historia y ninguna gráfica mezcla unidades.
const BLOQUES = [
  {
    id: "liquidez",
    label: "Liquidez",
    desc: "Importes monetarios en miles de €; liquidez corriente en veces.",
    grupos: [
      {
        titulo: "Liquidez corriente", unidad: "veces", formato: "veces", grafica: "Evolución de la liquidez corriente",
        filas: [{ label: "Liquidez corriente", get: (c) => c.ratios_liquidez?.liquidez_principal, fmt: "veces", bold: true }],
      },
      {
        titulo: "Fondo de maniobra y NOF", unidad: "miles de €", formato: "miles", grafica: "Fondo de maniobra y NOF",
        filas: [
          { label: "Fondo de maniobra", corto: "Fondo de maniobra", get: (c) => c.ratios_liquidez?.fondo_maniobra, fmt: "miles", bold: true },
          { label: "NOF (Necesidades Operativas de Fondos)", corto: "NOF", get: (c) => c.ratios_liquidez?.nof, fmt: "miles" },
          { label: "NOF ajustada (con tus exclusiones)", corto: "NOF ajustada", get: (c) => c.ratios_liquidez?.nof_ajustada, fmt: "miles" },
        ],
      },
    ],
  },
  {
    id: "solvencia",
    label: "Solvencia",
    desc: "Activo total sobre pasivo exigible, en veces.",
    grupos: [
      {
        titulo: "Solvencia", unidad: "veces", formato: "veces", grafica: "Evolución de la solvencia",
        filas: [{ label: "Solvencia (Activo total / Pasivo total)", corto: "Solvencia", get: (c) => c.ratios_solvencia?.solvencia_principal, fmt: "veces", bold: true }],
      },
    ],
  },
  {
    id: "endeudamiento",
    label: "Endeudamiento",
    desc: "Capacidad de devolución, estructura de la deuda, cobertura de gastos financieros e inversión.",
    grupos: [
      {
        titulo: "Capacidad de devolución", unidad: "veces (x)", formato: "x", grafica: "DFN / EBITDA y Deuda / Cash flow",
        filas: [
          { label: "DFN / EBITDA ajustado", corto: "DFN / EBITDA", get: (c) => c.ratios_endeudamiento?.dfn_ebitda, fmt: "x", bold: true, color: colorDfn },
          { label: "Deuda / Cash flow", corto: "Deuda / Cash flow", get: (c) => c.ratios_endeudamiento?.deuda_cash_flow, fmt: "x" },
        ],
      },
      {
        titulo: "Estructura del endeudamiento", unidad: "veces sobre patrimonio neto", formato: "veces", grafica: "Endeudamiento a largo y a corto plazo",
        filas: [
          { label: "Endeudamiento L/P", corto: "L/P", get: (c) => c.ratios_endeudamiento?.endeudamiento_lp, fmt: "veces" },
          { label: "Endeudamiento C/P", corto: "C/P", get: (c) => c.ratios_endeudamiento?.endeudamiento_cp, fmt: "veces" },
        ],
      },
      {
        titulo: "Cobertura de gastos financieros", unidad: "veces (x)", formato: "x", grafica: "EBITDA ajustado / Gastos financieros",
        filas: [
          { label: "EBITDA ajustado / Gastos financieros", corto: "EBITDA / Gastos financieros", get: (c) => c.ratios_endeudamiento?.ebitda_gastos_financieros, fmt: "x" },
        ],
      },
      {
        titulo: "Inversión", unidad: "% del EBITDA", formato: "pct", grafica: "Amortización / EBITDA (proxy CAPEX)",
        filas: [
          { label: "Amortización / EBITDA aj. (proxy CAPEX)", corto: "Amortización / EBITDA", get: (c) => c.ratios_endeudamiento?.capex_ebitda, fmt: "pct" },
        ],
      },
    ],
  },
  {
    id: "rotacion",
    label: "Rotación",
    desc: "Plazos medios en días, sobre los días reales del ejercicio.",
    grupos: [
      {
        titulo: "Plazos medios", unidad: "días", formato: "dias", grafica: "Plazos medios de cobro, pago y existencias",
        filas: [
          { label: "Plazo medio de cobro (PMC)", corto: "Cobro", get: (c) => c.ratios_rotacion?.plazo_cobro, fmt: "dias" },
          { label: "Plazo medio de pago (PMP)", corto: "Pago", get: (c) => c.ratios_rotacion?.plazo_pago, fmt: "dias" },
          { label: "Plazo medio de existencias (PME)", corto: "Existencias", get: (c) => c.ratios_rotacion?.plazo_existencias, fmt: "dias" },
        ],
      },
      {
        titulo: "Plazos ajustados (sin saldos excluidos)", unidad: "días", formato: "dias", grafica: "Plazos medios ajustados",
        filas: [
          { label: "PMC ajustado", corto: "Cobro ajustado", get: (c) => c.ratios_rotacion?.plazo_cobro_ajustado, fmt: "dias" },
          { label: "PMP ajustado", corto: "Pago ajustado", get: (c) => c.ratios_rotacion?.plazo_pago_ajustado, fmt: "dias" },
          { label: "PME ajustado", corto: "Existencias ajustado", get: (c) => c.ratios_rotacion?.plazo_existencias_ajustado, fmt: "dias" },
        ],
      },
    ],
  },
  {
    id: "personal",
    label: "Personal",
    desc: "Gastos de personal sobre ventas, en %.",
    grupos: [
      {
        titulo: "Personal", unidad: "% sobre ventas", formato: "pct", grafica: "Personal sobre ventas",
        filas: [{ label: "Personal / Ventas", corto: "Personal / Ventas", get: (c) => c.ratios_personal?.personal_ventas, fmt: "pct", bold: true }],
      },
    ],
  },
];

function VistaRatios({ serie, ctx, bloque, onBloque, serieUsaAcumulado, previsionVacia }) {
  const b = BLOQUES.find((x) => x.id === bloque) || BLOQUES[0];
  // Un grupo sin ningún dato en ningún año (p. ej. plazos ajustados sin
  // exclusiones configuradas) no se dibuja: la tabla lo enseña con N/D.
  const conDatos = (g) => serie.some((c) => !c.vacia && g.filas.some((f) => esNum(f.get(c))));
  return (
    <div className="space-y-5">
      <div className="pill-group flex-wrap" role="tablist" aria-label="Bloques de ratios">
        {BLOQUES.map((x) => (
          <button
            key={x.id}
            role="tab"
            aria-selected={x.id === b.id}
            onClick={() => onBloque(x.id)}
            className={cn("pill px-5", x.id === b.id && "pill-active")}
            data-testid={`bloque-${x.id}`}
          >
            {x.label}
          </button>
        ))}
      </div>
      <Card>
        <CabeceraBloque titulo={`${b.label} · Comparativa por ejercicio`} desc={b.desc} ctx={ctx} />
        <CardContent className="space-y-3">
          <AvisoSerie ctx={ctx} serieUsaAcumulado={serieUsaAcumulado} previsionVacia={previsionVacia} />
          <TablaAnios columnas={serie} ctx={ctx} filas={filasDeGrupos(b.grupos)} />
        </CardContent>
      </Card>
      {b.grupos.filter(conDatos).map((g) => (
        <GraficaGrupo key={g.titulo} grupo={g} serie={serie} ctx={ctx} serieUsaAcumulado={serieUsaAcumulado} />
      ))}
    </div>
  );
}
